import {EventEmitter} from 'events';
import {distinctUntilChanged, fromEventPattern, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {connect, Socket} from 'socket.io-client';

import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {pushState} from '../../../libs/shared/core/src/services/http/httpBase';
import {PushSocket} from './pushSocket';

export class SocketIOSocket extends EventEmitter implements PushSocket {
  constructor(url: string) {
    super();

    this.url = url;
  }

  private url: string;

  // Stateful socket.
  private socket: Socket | null = null;
  private isSocketConnected: boolean = false;
  private readonly rooms = new Set<string>();

  get isConnected() {
    return this.isSocketConnected;
  }

  get isSocketOpen() {
    return Boolean(this.socket);
  }

  get id() {
    if (!this.socket || !this.socket.id) {
      return null;
    }

    return this.socket.id;
  }

  connect() {
    // If we're already connected, nothing to do.
    if (this.socket) {
      return false;
    }

    // Otherwise, initiate a connection.
    this.socket = connect(this.url);

    // Register event handlers.
    const connected = () => {
      this.isSocketConnected = true;

      if (this.id) {
        pushState.defaultSocketId = this.id;
      }

      this.emit('connected');
    };

    const disconnected = () => {
      this.isSocketConnected = false;
      this.rooms.clear();
      this.emit('disconnected');
    };

    this.socket.on('connect', connected);
    this.socket.on('reconnect', connected);

    this.socket.on('disconnect', disconnected);
    this.socket.on('connect_error', disconnected);
    this.socket.on('connect_timeout', disconnected);

    this.socket.on('error', () => {
      // TODO: Log this.
    });

    return true;
  }

  disconnect() {
    // If we're not connected, nothing to do.
    if (!this.socket) {
      return false;
    }

    this.socket.disconnect();
    this.socket.removeAllListeners();
    this.socket = null;

    return true;
  }

  reconnect = () => {};

  subscribe(roomName: string) {
    // If we're not connected, throw.
    if (!this.socket) {
      throw new Error('Cannot subscribe when not connected.');
    }

    // Join the room.
    const {socket} = this;
    socket.emit('join', roomName);
    this.rooms.add(roomName);
    this.emit('_joined', roomName);

    // Return an unsubscribe method.
    return {
      unsubscribe: () => {
        // If we're disconnected, nothing to do.
        if (!this.socket || this.socket !== socket) {
          return;
        }

        // Otherwise, leave the room.
        socket.emit('leave', roomName);
        this.rooms.delete(roomName);
        this.emit('_left', roomName);
      },
    };
  }

  onPushEvent(eventName: string, callback: (data: any) => void) {
    if (!this.socket) {
      throw new Error('Cannot listen when not connected');
    }

    // Wrap the callback to prevent full crashes.
    const safeCallback = (data: any) => {
      // Skip this event if it originates from us (side-effect: hides own precogs).
      if (data && this.socket && data.web_socket_id === this.socket.id) {
        return;
      }

      try {
        callback(data);
      } catch (error) {
        reportToBugsnag(error);
      }
    };

    // Register for events on the socket.
    const {socket} = this;
    socket.on(eventName, safeCallback);

    // Return a stop listening method.
    return () => {
      // If we're disconnected, nothing to do.
      if (!this.socket || this.socket !== socket) {
        return;
      }

      // Otherwise, stop listening.
      socket.off(eventName, safeCallback);
    };
  }

  onDirectPushEvent(eventName: string, callback: (data: any) => void): () => void {
    return this.onPushEvent(eventName, callback);
  }

  emitDirectEvent(roomName: string, eventName: string, data: any): void {
    if (!this.socket) {
      throw new Error('Cannot emit event when not connected.');
    }

    const {socket} = this;
    // Socket.IO clients can't send to rooms directly because only the server knows about other clients
    socket.emit('directEvent', {
      roomName,
      eventName,
      message: data,
    });
  }

  ping(): void {
    if (!this.socket) {
      throw new Error('Cannot ping when not connected.');
    }

    this.socket.emit('ping', {});
  }

  getIsReceiving(roomName: string): boolean {
    return this.socket !== null && this.socket.connected && this.rooms.has(roomName);
  }

  getIsReceiving$(roomName: string): Observable<boolean> {
    return fromEventPattern(
      (handler) => {
        this.on('connected', handler);
        this.on('disconnected', handler);
        this.on('_joined', handler);
        this.on('_left', handler);
      },
      (handler) => {
        this.off('connected', handler);
        this.off('disconnected', handler);
        this.off('_joined', handler);
        this.off('_left', handler);
      },
    ).pipe(
      map(() => this.getIsReceiving(roomName)),
      distinctUntilChanged(),
    );
  }
}
